/**
 * Abertura da aba de depuração (spec 020). Não passa pelo login do Keycloak
 * nem pela sessão do admin: usa SÓ o token de depuração da aba. Lê a própria
 * sessão, registra o alvo como o usuário da aba (role client — o router manda
 * para o portal) e põe a faixa acima de tudo que a aba mostrar.
 *
 * Qualquer 401 depois disso — encerrada em Configurações, expirada, alvo
 * desativado — acaba a aba na tela de sessão encerrada, nunca no login.
 */
import { useEffect, useState, type ReactNode } from 'react';
import type { ImpersonationCurrent } from '@erp/shared';
import { api, setUnauthorizedHandler } from '@/lib/api-client';
import { endImpersonationLocally } from '@/auth/impersonation';
import { setSessionUser } from '@/auth/use-auth';
import { Spinner } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { ImpersonationBanner } from './ImpersonationBanner';

export function ImpersonationGate({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<ImpersonationCurrent | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    setUnauthorizedHandler(() => endImpersonationLocally('revoked'));
    let cancelled = false;
    void (async () => {
      const res = await api<ImpersonationCurrent>('/impersonation/current');
      if (cancelled) return;
      if (res.error) {
        // 401 já acabou a aba pelo handler; o resto é falha de rede ou da API
        if (res.error.status !== 401) setFailure(res.error.error);
        return;
      }
      const { target, startedAt } = res.data;
      setSessionUser({
        id: target.id,
        name: target.name,
        email: target.email,
        role: 'client',
        active: true,
        createdAt: startedAt,
      });
      setSession(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (failure) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background px-4">
        <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">
          <p className="text-sm text-muted-foreground">
            Não foi possível abrir a sessão de depuração ({failure}).
          </p>
          <Button size="sm" onClick={() => window.location.reload()}>
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <>
      <ImpersonationBanner session={session} />
      {children}
    </>
  );
}
